import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Target, Wind, Puzzle } from "lucide-react";
import { toast } from "sonner";
import { http, gradeLabel } from "@/lib/api";
import { StatsStrip } from "@/components/StatsStrip";
import { MyClasses } from "@/components/MyClasses";
import { RecordLinks } from "@/components/records/RecordShell";
import { GAME_INFO } from "@/components/lesson/games/GamePicker";

const MOOD = ["", "Heavy", "Restless", "Okay", "Calm", "Bright"];

const Entry = ({ e, onDelete }) => (
  <article className="rounded-3xl bg-[#F7F3EB] border border-[#E6DEC3] p-6 rise" data-testid={`journal-entry-${e.id}`}>
    <div className="flex justify-between gap-4">
      <div>
        <p className="text-xs uppercase tracking-widest text-[#C87D53] font-semibold">{e.subject_name} · {gradeLabel(e.grade)}</p>
        <h3 className="font-display text-xl text-[#2C4A3E] mt-1">{e.topic}</h3>
      </div>
      <button onClick={() => onDelete(e.id)} data-testid={`journal-delete-${e.id}`} className="text-stone-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
    </div>
    {e.intention && <p className="text-sm text-stone-500 mt-3 italic">Intention: "{e.intention}"</p>}
    {e.reflection && <p className="mt-3 text-stone-700 leading-relaxed whitespace-pre-line">{e.reflection}</p>}
    {e.drawing_id && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/journal/drawing/${e.drawing_id}`} alt="My reflection drawing" loading="lazy" data-testid={`journal-drawing-${e.id}`} className="mt-3 w-full aspect-[900/560] object-contain rounded-2xl border border-[#E6DEC3] bg-[#FBF8F1] animate-[pulse_2s_ease-in-out_1]" />}
    {e.game && <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#2E7D4F]" data-testid={`journal-game-${e.id}`}><Puzzle className="w-4 h-4" /> {GAME_INFO[e.game.kind || "sort"]?.name}: {e.game.correct}/{e.game.total} on the first try{e.game.seconds != null ? ` in ${e.game.seconds}s` : ""}</p>}
    {e.gratitude && <p className="text-sm text-stone-600 mt-3">Grateful for: {e.gratitude}</p>}
    <div className="flex flex-wrap gap-4 mt-5 text-xs text-stone-500">
      <span className="inline-flex items-center gap-1"><Target className="w-3.5 h-3.5" />{e.quiz_score}/{e.quiz_total}</span>
      <span className="inline-flex items-center gap-1"><Wind className="w-3.5 h-3.5" />{e.mindful_minutes} min</span>
      <span>{MOOD[e.mood_before]} → {MOOD[e.mood_after]}</span>
      <span>{new Date(e.created_at).toLocaleDateString()}</span>
    </div>
  </article>
);

export default function Journal() {
  const [entries, setEntries] = useState(null);
  const [key, setKey] = useState(0);
  const load = () => http.get("/journal").then((r) => setEntries(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    try { await http.delete(`/journal/${id}`); toast.success("Entry removed"); load(); setKey((k) => k + 1); }
    catch { toast.error("Could not remove entry"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12" data-testid="journal-page" data-translate>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E]">Reflection journal</h1>
      <p className="text-stone-500 mt-2">Your growth in awareness, one lesson at a time.</p>
      <RecordLinks studentId="me" className="mt-5" />
      <div className="mt-10"><StatsStrip key={key} /></div>
      <div className="mt-5"><MyClasses /></div>
      <div className="mt-10 grid md:grid-cols-2 gap-5" data-testid="journal-list">
        {entries?.map((e) => <Entry key={e.id} e={e} onDelete={del} />)}
      </div>
      {entries && !entries.length && (
        <div className="text-center py-16" data-testid="journal-empty">
          <p className="font-display text-2xl text-[#2C4A3E]">Your journal is waiting</p>
          <Link to="/" className="inline-block mt-5 px-6 py-3 rounded-full bg-[#2C4A3E] text-white" data-testid="journal-start-btn">Start a mindful lesson</Link>
        </div>
      )}
    </div>
  );
}
